import React from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import { connect, ConnectedProps } from 'react-redux';
import { Route, Routes } from 'react-router';
import * as Sentry from '@sentry/react';

import App from '../app/app';
import isInternalUi, { shouldValidateAuth } from '../app/isInternalUi';
import { processAuthenticatedUser } from '../app/utils';
import SSO from '../dashboard-pages/sso';
import GlobalCopyToast from '../global-copy-toast/GlobalCopyToast';
import GlobalToast from '../global-toast';
import Spinner from '../spinner';
import ForgotUserPassword from './forgot-password/forgot.password';
import Login from './login/login';
import AssistLogin from './assist-login';
import CliLogin from './cli-login';
import CreatePassword from './create-password';
import { PASSWORD_CREATION_IN_PROGRESS_KEY } from './create-password-form';
import HcRedirect from './hc-redirect';
import MagicLink from './magic-link';
import OrgDiscoveryLogin from './org-discovery-login';
import OrgNotFound from './org-not-found';
import SignUp from './signup';
import TrialExpired from './trial-expired';
import WithOrgSelected from './with-org-selector';
import { saveRoleData, saveUserData } from 'src/actions/user';
import { logout } from 'src/hooks/useLogout';
import i18n from 'src/locales';
import {
  selectUser,
  selectUserRole,
} from 'src/reducers/userReducer/userReducer';
import * as API from 'src/services/api';
import { IdPDetails } from 'src/services/apiInterfaces';
import { getZendeskUserInfo } from 'src/services/zendeskUser';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { RootState } from 'src/store/rootReducer';
import GlobalStyle from 'src/styles/globalStyle';
import {
  clearAssistExtensionCookies,
  sendSessionToAssistExtension,
  sendUserContextToAssistExtension,
  sendZendeskUserContextToAssistExtension,
} from 'src/utils/assistAuthUtils';
import { setCognitoNetworkErrorFixEnabled } from 'src/utils/authUtils';
import { basePath, withBase } from 'src/utils/basePath';
import {
  stashCliLoginRequest,
  takeStashedCliLoginPath,
} from 'src/utils/cliLoginUtils';
import { Routes as AppRoutes, UserRoles } from 'src/utils/enums';
import {
  getHelpCenterRedirectUrl,
  shouldRedirectToHelpCenter,
} from 'src/utils/helpCenterAuthUtils';
import { setSentryTraceIdTag } from 'src/utils/sentryTraceId';
import { setPreAuthParams } from 'src/utils/sessionUtils';
import { setSentryOrgIdTag } from 'src/utils/setSentryOrgIdTag';
import { withRouter, WithRouterProps } from 'src/utils/withRouter';
import {
  redirectToForethoughtDashboard,
  redirectToZendeskLogin,
} from 'src/utils/zendeskAuth';

const USER_EXISTS_ERROR_REGEX = /already.found.an.entry.for.username/gi;
const PRESIGNUP_FAILED_ERROR_REGEX =
  /PreSignUp.failed.with.error.User.does.not.exist/gi;
const INVALID_CREDS_MESSAGE = () =>
  i18n.t('authentication-wall:initApp.invalidCredentials');

// This version of the Amplify lib types 'data' as 'any', so that is what we use here. When we update that package, this should be fixed as well.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRoleFromUser(amplifyHubPayload: any): `${UserRoles}` | null {
  return amplifyHubPayload?.signInUserSession?.idToken?.payload?.[
    'cognito:groups'
  ]?.[0];
}

const INTERNAL_SUBDOMAINS = [
  'localhost',
  'dashboard-int',
  'dashboard-stage-int',
];

type MyState = {
  authState: string;
  errorMessage: string;
  isLoading: boolean;
  isSSOEnabled: boolean;
  isSSOEnforced: boolean;
  userRole: `${UserRoles}` | null;
};

class InitApp extends React.Component<
  PropsFromRedux & WithRouterProps,
  MyState
> {
  constructor(props: PropsFromRedux & WithRouterProps) {
    super(props);
    this.state = {
      authState: 'signIn',
      errorMessage: '',
      isLoading: true,
      isSSOEnabled: false,
      isSSOEnforced: false,
      userRole: null,
    };
  }

  async componentDidMount(): Promise<void> {
    stashCliLoginRequest();
    const propsUser = this.props.user;
    const { auth_time: sessionId } = propsUser?.user ?? {};
    const eventBody = {
      sessionId,
      userRole: this.props.userRole || this.state.userRole,
    };
    const queryParams = new URLSearchParams(this.props.location.search);

    let isSSOEnabled = false;
    let isSSOEnforced = false;

    // === Build-specific auth bootstrap ===
    if (IS_ZENDESK_BUILD) {
      // The trial-expired page is the one Zendesk destination that must not run
      // the /users/me probe: a suspended session 401s with `account_suspended`,
      // which routes the user here — re-probing would just bounce them back and
      // loop. Render the standalone page directly (see render()).
      if (this.props.location.pathname === AppRoutes.TRIAL_EXPIRED) {
        this.setState({ isLoading: false });
        return;
      }
      // Zendesk build: every authenticated request rides on the Zendesk SSO
      // cookies, and there is no Cognito session. We treat /users/me as the
      // session probe — if it succeeds we seed Redux + auth state and mount
      // <App />; if it 401/403s, the HTTP layer's auth-failure funnel
      // redirects to login.
      try {
        const info = await getZendeskUserInfo();
        if (
          info.forethoughtDashboardRedirect &&
          redirectToForethoughtDashboard(info.forethoughtDashboardRedirect)
        ) {
          return;
        }
        this.props.saveUserData({ user: info.user });
        this.props.saveRoleData(info.role);
        // Mirror the role-and-observability side effects the standard build
        // performs from the Cognito ID token, but sourced from /users/me:
        //   1. localStorage so the synchronous `getUserRole()` helper
        //      (utils/enums.tsx) returns a value — many call sites, including
        //      App.componentDidMount's permissions gate, depend on it.
        //   2. Sentry user/org/trace tags for observability parity.
        //   3. Salesforce contact sync for non-agent users.
        // Skipped vs. setUserRole: the public/internal-UI host-mismatch
        // redirect — ZD runs on Zendesk subdomains, neither host applies.
        localStorage.setItem('userRole', info.role);
        Sentry.setUser({ email: info.user.email, role: info.role });
        setSentryOrgIdTag();
        setSentryTraceIdTag();
        if (info.role !== UserRoles.ROLE_AGENT) {
          void processAuthenticatedUser(info.user.email);
        }
        sendZendeskUserContextToAssistExtension(info.user.email);
        this.setState({
          authState: 'signedIn',
          userRole: info.role,
        });
      } catch {
        //For Zendesk, failing this section means you aren't authenticated
        redirectToZendeskLogin();
        return;
      }
    } else {
      const cognitoErrorMessage = queryParams.get('error_description');

      // Due to a known issue with Cognito, users have to go through the SSO page twice when signing up the first time.
      // This will catch an error and redirect them back to the single-sign-on page.
      if (
        cognitoErrorMessage &&
        USER_EXISTS_ERROR_REGEX.test(cognitoErrorMessage.toString())
      ) {
        this.props.navigate(AppRoutes.SINGLE_SIGN_ON);
      }

      if (
        cognitoErrorMessage &&
        PRESIGNUP_FAILED_ERROR_REGEX.test(cognitoErrorMessage.toString())
      ) {
        this.props.setGlobalToastOptions({
          subtitle: i18n.t('authentication-wall:initApp.noAccess.subtitle'),
          title: i18n.t('authentication-wall:initApp.noAccess.title'),
          variant: 'danger',
        });
        this.props.navigate('/');
      }

      let provider: IdPDetails | null = null;
      const subdomain = window.location.hostname.split('.')[0];

      // if subdomain is not a reserved internal subdomain, fetch org-specific idp/sso details based on subdomain
      if (!INTERNAL_SUBDOMAINS.includes(subdomain)) {
        try {
          const {
            cognito_network_error_fix_enabled,
            idp_details,
            is_sso_enabled,
            is_sso_enforced,
          } = await API.getOrgDiscovery(subdomain);
          setCognitoNetworkErrorFixEnabled(cognito_network_error_fix_enabled);
          provider = idp_details;
          isSSOEnabled = is_sso_enabled;
          isSSOEnforced = is_sso_enforced;
        } catch (error) {
          console.error(
            `error fetching identity provider: ${JSON.stringify(error)}`,
          );
        }
      }

      Amplify.configure({
        Auth: {
          oauth: {
            domain: DOMAIN,
            // When the dashboard is mounted at a sub-path (see BASE_PATH in
            // rsbuild.config.ts), Cognito must redirect back to that mount,
            // not to the bare origin.
            redirectSignIn: `${window.location.origin}${basePath}`,
            redirectSignOut: `${window.location.origin}${withBase('/logout')}`,
            responseType: 'code',
            scope: ['email', 'openid', 'profile'],
          },
          region: 'us-west-2a',
          userPoolId: COGNITO_USER_POOL,
          userPoolWebClientId: provider?.pool_client_id || COGNITO_WEB_POOL,
          // Custom Cognito IDP endpoint (floci in scooter dev envs). A relative
          // path (e.g. '/cognito') resolves against the page origin, where the
          // dev server proxies it to the IdP (server.proxy in rsbuild.config.ts)
          // — keeping the browser's Cognito calls same-origin, so no CORS.
          // Empty string = real AWS Cognito.
          ...(COGNITO_ENDPOINT
            ? {
                endpoint: COGNITO_ENDPOINT.startsWith('/')
                  ? `${window.location.origin}${COGNITO_ENDPOINT}`
                  : COGNITO_ENDPOINT,
              }
            : {}),
        },
      });

      Hub.listen('auth', async data => {
        // skip auth state updates during password creation flow to prevent race conditions
        if (sessionStorage.getItem(PASSWORD_CREATION_IN_PROGRESS_KEY)) {
          return;
        }

        switch (data.payload.event) {
          case 'signIn':
            setPreAuthParams();

            const { origin } = window.location;
            if (shouldRedirectToHelpCenter()) {
              try {
                const redirectUrl = await getHelpCenterRedirectUrl();
                window.location.href = redirectUrl;
                return;
              } catch (err) {
                Sentry.captureException(err);
              }
            }

            try {
              const role = getRoleFromUser(data.payload.data);
              if (shouldValidateAuth(role)) {
                await API.validateUserAuth(origin);
              }
            } catch {
              Auth.signOut();
              this.setState({
                errorMessage: INVALID_CREDS_MESSAGE(),
              });
            }
            // Tell the assist extension the auth state has changed and forward
            // the signed-in user's email + tenant subdomain so it can pre-fill
            // the sign-in page next round-trip and (eventually) drive the
            // cookie-mode API routing.
            sendSessionToAssistExtension();
            sendUserContextToAssistExtension();
            this.setState({
              authState: 'signedIn',
              userRole: getRoleFromUser(data.payload.data),
            });
            const cliLoginPath = takeStashedCliLoginPath();
            if (cliLoginPath) {
              this.props.navigate(cliLoginPath);
            }
            API.emitTrackingEventApi({
              ...eventBody,
              eventType: 'dashboard-sign-in',
            });
            break;
          case 'signIn_failure':
            this.setState({ authState: 'signIn' });
            break;
          case 'signOut':
          case 'oAuthSignOut':
            // Tell the assist extension the auth state has changed and drop the
            // ZD session cookies it may have been using for cookie-only auth.
            sendSessionToAssistExtension();
            clearAssistExtensionCookies();
            this.setState({ authState: 'signOut' });
            API.emitTrackingEventApi({
              ...eventBody,
              eventType: 'dashboard-sign-out',
            });
            break;
          case 'customOAuthState':
            const url = atob(unescape(data.payload.data));
            // Amplify captures window.location.pathname before the OAuth
            // redirect, which already includes BASE_PATH under a mounted
            // deployment. Only re-prefix if the stored URL isn't already
            // inside the mount.
            const target = url.startsWith(basePath) ? url : withBase(url);
            if (window.location.pathname !== target) {
              window.location.href = `${window.location.origin}${target}`;
            }
            break;
          case 'tokenRefresh_failure':
            Sentry.withScope(scope => {
              scope.setExtra('data', data);
              Sentry.captureMessage(data.payload.event);
            });
            API.emitTrackingEventApi({
              ...eventBody,
              eventType: 'dashboard-sign-out',
            });
            break;
          default:
            break;
        }
      });
    }

    // === Common: signout query param + assist extension session ===
    // Every time the page loads, send the current auth state to the assist
    // extension. If the `signout` query param is set, log out the current
    // user and remove the parameter.
    if (queryParams.has('signout')) {
      queryParams.delete('signout');
      this.props.navigate(
        {
          search: queryParams.toString(),
        },
        { replace: true },
      );
      if (IS_ZENDESK_BUILD) {
        await logout(sessionId, this.props.userRole);
      } else {
        await API.emitTrackingEventApi({
          ...eventBody,
          eventType: 'dashboard-sign-out',
        });
        await Auth.signOut();
      }
    } else {
      sendSessionToAssistExtension();
      sendUserContextToAssistExtension();
    }

    // === Build-specific session probe ===
    if (IS_ZENDESK_BUILD) {
      // ZD: bootstrap above already determined signed-in state; just run the
      // helpcenter redirect (when applicable) and drop the spinner.
      if (this.state.authState === 'signedIn' && shouldRedirectToHelpCenter()) {
        try {
          const redirectUrl = await getHelpCenterRedirectUrl();
          window.location.href = redirectUrl;
          return;
        } catch (err) {
          Sentry.captureException(err);
        }
      }
      this.setState({ isLoading: false });
    } else {
      try {
        const data = await Auth.currentAuthenticatedUser();
        if (data) {
          if (shouldRedirectToHelpCenter()) {
            try {
              const redirectUrl = await getHelpCenterRedirectUrl();
              window.location.href = redirectUrl;
              return;
            } catch (err) {
              Sentry.captureException(err);
            }
          }

          const userRole = getRoleFromUser(data);
          this.setState({
            authState: 'signedIn',
            isLoading: false,
            isSSOEnabled,
            isSSOEnforced,
            userRole,
          });
        }
      } catch {
        if (this.state.authState !== 'signedIn') {
          this.setState({
            authState: 'signIn',
            isLoading: false,
            isSSOEnabled,
            isSSOEnforced,
          });
        }
      }
    }
  }

  render() {
    const { authState, isLoading, isSSOEnabled, isSSOEnforced, userRole } =
      this.state;
    const isSignedIn = authState === 'signedIn';
    const isSuperAdmin = userRole === UserRoles.ROLE_SUPER_ADMIN;

    if (isLoading) {
      return <Spinner />;
    }

    // Zendesk build never shows the standard login routes — the only
    // unauthenticated destination is the Zendesk signin page (reached via
    // redirectToZendeskLogin / maybeHandleZendeskAuthFailure). The exception is
    // the trial-expired page, which a suspended session is routed to and must
    // render in place. Otherwise, while the top-level navigation to signin is
    // in flight, keep the spinner up.
    if (IS_ZENDESK_BUILD && !isSignedIn) {
      if (this.props.location.pathname === AppRoutes.TRIAL_EXPIRED) {
        return (
          <>
            <GlobalStyle />
            <TrialExpired />
          </>
        );
      }
      return <Spinner />;
    }

    return (
      <>
        {/*
          Global components go here if they are needed in logged-in and logged-out state.
          If the component requires user auth it belongs in <App /> or further down in the component tree.
        */}
        <GlobalStyle />
        <GlobalToast />
        <GlobalCopyToast />
        <Routes>
          <Route element={<SSO />} path={AppRoutes.SINGLE_SIGN_ON} />
          {isSignedIn ? (
            <>
              <Route element={<HcRedirect />} path={AppRoutes.HC_REDIRECT} />
              <Route element={<CliLogin />} path={AppRoutes.CLI_LOGIN} />
              <Route
                element={
                  isSuperAdmin ? (
                    <WithOrgSelected>
                      <App />
                    </WithOrgSelected>
                  ) : (
                    <App />
                  )
                }
                path='*'
              />
            </>
          ) : (
            <>
              <Route element={<AssistLogin />} path={AppRoutes.ASSIST_LOGIN} />
              <Route element={<OrgDiscoveryLogin />} path={AppRoutes.LOGIN} />
              <Route
                element={<CreatePassword />}
                path={AppRoutes.CREATE_PASSWORD}
              />
              <Route element={<OrgNotFound />} path={AppRoutes.ORG_NOT_FOUND} />
              <Route element={<MagicLink />} path={AppRoutes.MAGIC_LINK} />
              <Route
                element={<ForgotUserPassword />}
                path={AppRoutes.FORGOT_PASSWORD}
              />
              {isInternalUi() ? (
                <Route element={<SignUp />} path={AppRoutes.SIGN_UP} />
              ) : null}
              <Route
                element={
                  <Login
                    authValidationError={this.state.errorMessage}
                    isSSOEnabled={isSSOEnabled}
                    isSSOEnforced={isSSOEnforced}
                  />
                }
                path='*'
              />
            </>
          )}
        </Routes>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: selectUser(state),
  userRole: selectUserRole(state),
});

const connector = connect(mapStateToProps, {
  saveRoleData,
  saveUserData,
  setGlobalToastOptions,
});
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedInitApp = connector(
  withRouter<PropsFromRedux & WithRouterProps>(InitApp),
);

export default ConnectedInitApp;
